import { CopyOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  ConfigProvider,
  Empty,
  Pagination,
  PaginationProps,
  Select,
  SelectProps,
  Spin,
  Table,
  Tag,
} from "antd";
import { AnyObject } from "antd/es/_util/type";
import dayjs from "dayjs";
import { debounce } from "lodash";
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { selectViewShowBy } from "../../constants/selectViewShowBy";
import { STATUSES_APPLICATION_BANKS_TO_SIGN_IN } from "../../constants/statusesApplicationBanksToSignIn";
import { APPLICATION_STATUS_VALUE_FILTER } from "../../enums/applicationStatusFilterValueEnum";
import { IFilterApplicationForm } from "../../interfaces/Application/FilterFrame/IFilterApplicationForm";
import { IResponseApplicationsParams } from "../../interfaces/Application/IResponseApplicationsParams/IResponseApplicationsParams";
import { ITableApplication } from "../../interfaces/ItableApplication";
import { IDownloadDocumentHandlerProps } from "../../interfaces/props/IDownloadDocumentHandlerProps";
import { IFilterFrameProps } from "../../interfaces/props/IFilterFrameProps";
import { useLazyGetFilteredListApplicationQuery } from "../../store/api/application.api";
import {
  ApplicationBanks,
  StatusObj,
} from "../../store/slice/authSliceLogin/authSliceLogin";
import { DocumentToSign } from "../../store/slice/requestAppSlise/requestAppSlise";
import { APPLICATION_BANK_STATUS_KKB } from "../../utils/guards/ApplicationBankStatusGuard/types";
import { DownloadingFiles } from "../../utils/utilities/Downloads";
import { getSearchParamFromURL } from "../../utils/utilities/getSearchParamFromUrl";
import { withLoadingHidePagination } from "../../views/pageApplications/helpers/withLoadingHidePagination";
import { FilterFrame } from "../Application/FilterFrame/FilterFrame";
import { DocsForSigning } from "./features/DocsForSigning/docsForSigning";
import { ModalSigning } from "./features/ModalSigning/ModalSigning";

import classes from "./RequiringSigningDocument.module.css";
import { formatPrice } from "../../utils/utilities/formatPrice";

/**
 * Компонент отображения документов, требующих подписи.
 */
export function RequiringSigningDocsModule(): ReactElement {
  const [expandableRowKeys, setExpandableRowKeys] = useState<string[]>([]);
  const [limit, setLimit] = useState(selectViewShowBy[0].value);
  const [pageNumber, setPageNumber] = useState(1);
  const [isHidePagination, setIsHidePagination] = useState(false);
  const [documentsToSignIn, setObjectWithDocsToSignIn] = useState<
    ApplicationBanks[]
  >([]);

  const [trigger, { data: dataApplications, isFetching }] =
    useLazyGetFilteredListApplicationQuery();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterFormState = useRef<IFilterApplicationForm>({});

  const setInitialStateComponent = async () => {
    setPageNumber(1);
    await withLoadingHidePagination(setIsHidePagination, () =>
      trigger(
        {
          page: 1,
          limit,
          status_preset: APPLICATION_STATUS_VALUE_FILTER.SIGNATURE_REQUIRED,
        },
        true,
      ).unwrap(),
    );
  };

  useEffect(() => {
    setInitialStateComponent();
  }, [searchParams]);

  const debouncedSearch = useCallback(
    debounce(async (value: Omit<IResponseApplicationsParams, "page">) => {
      setPageNumber(1);
      const status_preset = getSearchParamFromURL("status_preset");
      withLoadingHidePagination(setIsHidePagination, () => {
        return trigger(
          {
            ...value,
            page: 1,
            status_preset: status_preset || undefined,
          },
          true,
        ).unwrap();
      });
    }, 500),
    [searchParams],
  );

  const totalPages = dataApplications?.applications.last_page;
  const transformDataForApplications: ITableApplication[] = dataApplications
    ?.applications?.data
    ? dataApplications.applications?.data.map((el) => {
        const { start_bg_at, finish_bg_at, application_banks } = el;
        const termValue = dayjs(finish_bg_at).diff(dayjs(start_bg_at), "day");
        const applicationsToSignIn = application_banks.filter((el) =>
          STATUSES_APPLICATION_BANKS_TO_SIGN_IN.includes(
            el.status.type as APPLICATION_BANK_STATUS_KKB,
          ),
        );
        const baksOnlyWithDocksTiSigning = application_banks
          .filter((el) =>
            STATUSES_APPLICATION_BANKS_TO_SIGN_IN.includes(
              el.status.type as APPLICATION_BANK_STATUS_KKB,
            ),
          )
          .reduce((acc: DocumentToSign[], item: ApplicationBanks) => {
            if (item?.documents) {
              acc = [...acc, ...item.documents];
              return acc.flat();
            }

            return acc;
          }, []);

        return {
          id: el.id,
          key: el.id,
          status: el.status,
          organizationName: el.organization.name,
          organizationInn: el.organization.inn,
          applicationSumBg: el.application_sum_bg,
          term: termValue,
          bgTypeName: el.bg_type.name,
          purchaseFz: el.purchase.fz,
          purchaseNumber: el.purchase.purchase_number,
          docs: baksOnlyWithDocksTiSigning,
          applications: applicationsToSignIn,
        };
      })
    : [];

  function handleToggleExpandRow(record: AnyObject) {
    setExpandableRowKeys((prev) =>
      prev.includes(record.key)
        ? prev.filter((key) => key !== record.key)
        : [...prev, record.key],
    );
  }

  const onHandleSelect: SelectProps["onChange"] = async (value) => {
    setLimit(value);
    setPageNumber(1);

    const status_preset = getSearchParamFromURL("status_preset") || undefined;

    withLoadingHidePagination(setIsHidePagination, () => {
      return trigger({
        page: 1,
        limit: value,
        status_preset,
        ...filterFormState.current,
      }).unwrap();
    });
  };

  const onHandleFilterForm: IFilterFrameProps["onChangeForm"] = (value) => {
    filterFormState.current = value;
    debouncedSearch({ ...value, limit });
  };

  const onHandleResetForm: IFilterFrameProps["onResetForm"] = async () => {
    setPageNumber(1);
    filterFormState.current = {};
    const status_preset = searchParams.get("status_preset");

    withLoadingHidePagination(setIsHidePagination, () =>
      trigger(
        {
          page: 1,
          limit: limit,
          status_preset: status_preset || undefined,
        },
        true,
      ).unwrap(),
    );
  };

  const onHandleChangePagination: PaginationProps["onChange"] = (
    page: number,
  ) => {
    const status_preset = searchParams.get("status_preset");
    setPageNumber(page);
    trigger(
      {
        page: page,
        limit,
        status_preset: status_preset || undefined,
      },
      true,
    );
  };

  const addDocumentsToSignIn = (
    el: ApplicationBanks[],
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    const appId = el[0].id;

    if ((event.target as HTMLInputElement).checked) {
      setObjectWithDocsToSignIn([...documentsToSignIn, ...el]);
      return;
    }

    const newData = documentsToSignIn.filter((item) => item.id !== appId);
    setObjectWithDocsToSignIn(newData);
  };

  const downloadDocumentCallback = ({
    name,
    document_id,
    applicationBankId,
  }: IDownloadDocumentHandlerProps) => {
    const urlFile = {
      url: `auth/applications/system-bank/create-data-to-bank/${applicationBankId}/file/${document_id}`,
      fileName: `${name}`,
    };
    DownloadingFiles(urlFile);
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <FilterFrame
          onChangeForm={onHandleFilterForm}
          onResetForm={onHandleResetForm}
        />
      </div>
      <div className={classes.mainContent}>
        <ConfigProvider
          theme={{
            components: {
              Table: {
                borderRadius: 12,
                headerBorderRadius: 12,
                headerBg: "transparent",
                rowExpandedBg: "transparent",
                colorBorder: "black",
                colorBorderBg: "black",
              },
            },
          }}
        >
          {transformDataForApplications.length === 0 ? (
            <Spin size="large" spinning={isFetching} />
          ) : (
            <Table<ITableApplication>
              rowClassName={classes.tableRow}
              dataSource={transformDataForApplications}
              className={classes.table}
              loading={{ spinning: isFetching, size: "large" }}
              expandable={{
                expandedRowRender: (el) => (
                  <DocsForSigning
                    docs={el.docs}
                    download={downloadDocumentCallback}
                  />
                ),
                showExpandColumn: false,
                expandedRowKeys: expandableRowKeys,
                expandedRowClassName: classes.tableRowExpand,
              }}
              pagination={false}
            >
              <Table.Column
                className={classes.selectCol}
                render={(el) => {
                  return (
                    <Checkbox
                      onClick={(event) => addDocumentsToSignIn(el, event)}
                    />
                  );
                }}
                title="Выбрать"
                dataIndex="applications"
                key="select"
                width={51}
              />
              <Table.Column
                title="№ Заявки"
                dataIndex="id"
                key="id"
                render={(id) => {
                  return (
                    <span
                      style={{ color: "#3c79ff" }}
                      onClick={() => navigate(`/application/${id}`)}
                    >
                      №{id} <CopyOutlined />
                    </span>
                  );
                }}
              />
              <Table.Column<ITableApplication["status"]>
                title="Статус заявки"
                dataIndex="status"
                key="status"
                minWidth={150}
                render={(value: StatusObj) => {
                  return (
                    <Tag className={classes.tag} color={value?.color}>
                      {value?.title}
                    </Tag>
                  );
                }}
              />
              <Table.Column
                title="Клиент"
                minWidth={200}
                dataIndex="organizationName"
                key="organizationName"
              />
              <Table.Column
                title="ИНН"
                dataIndex="organizationInn"
                key="organizationInn"
                width={50}
              />
              <Table.Column
                title="Сумма БГ"
                dataIndex="applicationSumBg"
                key="applicationSumBg"
                render={(value) => {
                  return <>{formatPrice(value)}</>;
                }}
              />
              <Table.Column
                minWidth={50}
                title="Срок"
                dataIndex="term"
                key="term"
              />
              <Table.Column
                title="Тип заявки"
                dataIndex="bgTypeName"
                key="bgTypeName"
              />
              <Table.Column
                title="ФЗ"
                width={60}
                dataIndex="purchaseFz"
                key="purchaseFz"
              />
              <Table.Column
                title="Номер закупки"
                dataIndex="purchaseNumber"
                key="purchaseNumber"
              />
              <Table.Column
                title="Документы"
                dataIndex="action"
                key="action"
                render={(_, record) => {
                  return (
                    <Button
                      type="link"
                      className={classes.toggleExpandRowBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleExpandRow(record);
                      }}
                    >
                      {expandableRowKeys.includes(record.key)
                        ? "Свернуть"
                        : "Раскрыть"}
                    </Button>
                  );
                }}
              />
            </Table>
          )}
        </ConfigProvider>
      </div>

      <div className={classes.paginationContainer}>
        <div>
          Показывать по:{" "}
          <Select
            onChange={onHandleSelect}
            value={limit}
            options={selectViewShowBy}
            className={classes.paginationSelect}
          />
        </div>

        {totalPages && !isHidePagination ? (
          <Pagination
            className={classes.pagination}
            total={totalPages}
            current={pageNumber}
            onChange={onHandleChangePagination}
            defaultPageSize={1}
          />
        ) : null}
      </div>
      <ModalSigning
        applications={documentsToSignIn}
        disabled={!documentsToSignIn.length}
      />
    </div>
  );
}
